import React from 'react';
import PropTypes from 'prop-types';

import Spinner from '../Spinner';

import { isString } from '../../Utils/checkVarType';
import isValidVar from '../../Utils/checkVarType';
import styles, { mainCls } from './styles';
import createStyle from '../../Utils/createStyle';
import makeClassName from '../../Utils/makeClassName';

const useStyles = createStyle(styles);

/**
 * Basic button component
 */
export default function Button({
  children,
  className,
  action,
  type,
  color,
  bg,
  size,
  border,
  wrap,
  disabled,
  loading,
  inline,
  value,
  ...props
}) {
  const cls = useStyles();
  const sizes = {
    s: `${cls[`${mainCls}-s--s`]} tp-w--l tp-s--m`,
    m: `${cls[`${mainCls}-s--m`]} tp-w--m tp-s--l`,
    l: 'pt--m pb--m pr--l pl--l tp-w--m tp-s--xl',
  };

  const classes = makeClassName([
    cls[mainCls],
    'is-clickable b--s tp-a--c',
    className,
    sizes[size] || '',
    inline ? cls[`${mainCls}--inline`] : '',
    color ? `c-txt--${color}` : '',
    bg ? `c-bg--${bg}` : '',
    !wrap ? 'tp--ell' : '',
    disabled ? 'is-inactive' : '',
    border ? `c-br--${border}` : 'c-br--tr',
  ]);

  const isA = isString(action);
  const Button = isA ? 'a' : 'button';
  const prop = {
    ...props,
    [isA ? 'href' : 'onClick']: action,
    ...!isA && { type },
    ...!isA && disabled && { disabled },
  };

  return (
    <Button className={classes} {...prop}>
      { !!loading && <Spinner className="tp-va--m mr--s" inline={false} color="w" /> }
      { value || children }
    </Button>
  );
};

Button.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Action to perform, could be an URL (as string) or any callback (as a function)
   */
  action: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),

  /**
   * Type of button (eg. 'submit', 'reset')
   */
  type: PropTypes.string,

  /**
   * Text color
   */
  color: PropTypes.string,

  /**
   * Background color
   */
  bg: PropTypes.string,

  /**
   * Border color
   */
  border: PropTypes.string,

  /**
   * Button size
   */
  size: PropTypes.string,

  /**
   * Allow wrap of inner text
   */
  wrap: PropTypes.bool,

  /**
   * Disable state
   */
  disabled: PropTypes.bool,

  /**
   * Loading state
   */
  loading: PropTypes.bool,

  /**
   * Make the button behave like an inline element
   */
  inline: PropTypes.bool,

  /**
   * Inner content
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
}

Button.defaultProps = {
  className: '',
  action: () => {},
  type: 'button',
  color: 'w',
  bg: 'primary',
  size: 'm',
  border: '',
  wrap: false,
  disabled: false,
  loading: false,
  inline: false,
  value: '',
}

